import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Link2, Wallet, TrendingUp, Eye, ScrollText, ShieldCheck, Briefcase, Megaphone,
  ArrowRight, Mail, Sparkles, MousePointerClick, UserPlus, Check, Instagram, Linkedin, MessageCircle, Mic,
} from "lucide-react";
import distApi from "@/lib/distApi";
import "./demo.css";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] },
};

const Label = ({ children, light }) => (
  <p className={`text-xs font-bold tracking-[0.2em] uppercase mb-4 ${light ? "text-white/50" : "text-[#86868B]"}`}>{children}</p>
);

/* ---------- Hero visual: your link, orbited by the places you'll share it ---------- */
const LinkHeroMock = ({ g }) => (
  <div data-testid="dl-share-mock" className="relative mx-auto lg:mx-0 w-full max-w-[420px]">
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
      className="rounded-2xl border border-black/10 bg-white shadow-[0_24px_80px_rgba(0,0,0,0.10)] p-5">
      <p className="text-[11px] font-semibold uppercase tracking-widest text-[#86868B] mb-3">{g("link_label", "Your link — yours alone")}</p>
      <div className="rounded-xl bg-[#F5F5F7] border border-black/5 px-4 py-3 flex items-center gap-2.5">
        <Link2 size={15} className="text-[#0071E3]" />
        <span className="text-[13px] font-mono text-[#1d1d1f]/80 truncate">yourapp.com/d/you</span>
        <span className="ml-auto text-[12px] font-semibold text-white bg-[#0071E3] rounded-full px-3 py-1">{g("link_copy", "Copy")}</span>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-4">
        {[{ i: MousePointerClick, l: g("stat_clicks", "Clicks") }, { i: UserPlus, l: g("stat_signups", "Signups") }, { i: Wallet, l: g("stat_earnings", "Earnings") }].map(({ i: I, l }, k) => (
          <div key={k} className="rounded-xl bg-[#F5F5F7] p-3">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[#86868B] flex items-center gap-1"><I size={11} /> {l}</p>
            <div className="mt-2 space-y-1.5">
              <div className="h-1.5 rounded-full bg-[#0071E3]/60" style={{ width: `${80 - k * 12}%` }} />
              <div className="h-1.5 rounded-full bg-[#0071E3]/25" style={{ width: `${55 - k * 8}%` }} />
            </div>
          </div>
        ))}
      </div>
    </motion.div>
    {[{ Icon: Instagram, cls: "-top-4 -left-4", d: 0.5 }, { Icon: Linkedin, cls: "top-10 -right-5", d: 0.65 },
      { Icon: MessageCircle, cls: "-bottom-4 left-10", d: 0.8 }, { Icon: Mail, cls: "-bottom-5 right-8", d: 0.95 }].map(({ Icon, cls, d }, i) => (
      <motion.div key={i} initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4, delay: d }}
        className={`absolute ${cls} w-10 h-10 rounded-2xl bg-white border border-black/10 shadow-lg flex items-center justify-center text-[#1d1d1f]/70`}>
        <Icon size={16} />
      </motion.div>
    ))}
  </div>
);

/* ---------- Compact WhatsApp share visual ---------- */
const ChatMock = () => (
  <div data-testid="dl-chat-mock" className="rounded-[1.8rem] border border-black/10 bg-white shadow-[0_18px_60px_rgba(0,0,0,0.10)] overflow-hidden max-w-[360px] mx-auto lg:mx-0">
    <div className="bg-[#075E54] text-white px-4 py-3 flex items-center gap-2.5">
      <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center"><Sparkles size={13} /></div>
      <p className="text-[13px] font-semibold">Their new email agent</p>
    </div>
    <div className="bg-[#ECE5DD] px-3.5 py-4 space-y-2">
      <div className="flex justify-end"><div className="bg-[#DCF8C6] rounded-2xl rounded-br-md px-3 py-2 text-[12.5px] max-w-[80%]">Anything important this morning?</div></div>
      <div className="flex justify-start"><div className="bg-white rounded-2xl rounded-bl-md px-3 py-2 text-[12.5px] max-w-[85%]">3 things: a VIP moved your demo, a contract arrived, 2 invoices need approval. The rest — archived.</div></div>
      <div className="flex justify-end"><div className="bg-[#DCF8C6] rounded-2xl rounded-br-md px-3 py-2 text-[12.5px] max-w-[80%]">This is the thing Ana shared. I get it now.</div></div>
    </div>
    <div className="bg-[#F7F7F7] border-t border-black/5 px-3.5 py-2.5 flex items-center gap-2">
      <div className="flex-1 bg-white rounded-full border border-black/10 px-3.5 py-1.5 text-[12px] text-[#86868B]">Message</div>
      <div className="w-8 h-8 rounded-full bg-[#075E54] flex items-center justify-center text-white"><Mic size={13} /></div>
    </div>
  </div>
);

/* ---------- Animated growth mock (kept — NO currency, NO %) ---------- */
const GrowthMock = () => {
  const bars = [18, 30, 42, 56, 72, 92];
  return (
    <div data-testid="dl-growth-mock" className="flex items-end gap-3 h-40 mt-2" aria-hidden="true">
      {bars.map((h, i) => (
        <motion.div key={i} initial={{ height: 0 }} whileInView={{ height: `${h}%` }} viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 * i, ease: [0.25, 0.1, 0.25, 1] }}
          className="flex-1 rounded-t-lg bg-gradient-to-t from-[#0071E3] to-[#4DA3FF]" />
      ))}
    </div>
  );
};

const buildPersonas = (g) => [
  {
    icon: Briefcase, testid: "dl-persona-reseller", eyebrow: g("p1_eyebrow", "For seasoned distributors"),
    title: g("p1_title", "You've sold software before. This one closes itself."),
    points: [
      g("p1_b1", "The demo is a WhatsApp message — your prospect texts their own inbox and gets it in a minute"),
      g("p1_b2", "Recurring earnings on the payments your referrals keep making, not a one-off bounty"),
      g("p1_b3", "A panel your accountant would approve of: every click, signup and payment, to the cent"),
    ],
  },
  {
    icon: Megaphone, testid: "dl-persona-creator", eyebrow: g("p2_eyebrow", "For creators & influencers"),
    title: g("p2_title", "Your audience already complains about email. Monetize the fix."),
    points: [
      g("p2_b1", "One link — bio, stories, newsletter, community. Post it once, it keeps working"),
      g("p2_b2", "Your followers get their evenings back; you earn while you sleep"),
      g("p2_b3", "No inventory, no support tickets, no ad budget. Just the link"),
    ],
  },
];

const buildSteps = (g) => [
  { n: "01", title: g("step1_title", "Get your personal link."), body: g("step1_body", "Free, 60 seconds, no paperwork. The link is yours alone — wherever it travels, the credit comes home to you.") },
  { n: "02", title: g("step2_title", "Share it where inboxes hurt."), body: g("step2_body", "Founders, agencies, your followers, that friend who answers email at midnight. You know exactly who needs this.") },
  { n: "03", title: g("step3_title", "Earn on every payment."), body: g("step3_body", "Someone joins through your link and pays — you earn. They pay again next month — you earn again. From day one.") },
];

const buildTrust = (g) => [
  { icon: Eye, title: g("t1_title", "See everything, live."), body: g("t1_body", "Every click, every signup, every payment — in your panel the moment it happens. No monthly reports, no asking.") },
  { icon: Wallet, title: g("t2_title", "A wallet you can audit."), body: g("t2_body", "Your full earnings and payout history, entry by entry, with the math shown. What you see is what you're owed.") },
  { icon: ScrollText, title: g("t3_title", "Clear terms. No fine print."), body: g("t3_body", "The distributor terms are public and written in plain language. Read them before you sign up — we want partners, not surprises.") },
  { icon: ShieldCheck, title: g("t4_title", "Real people behind payouts."), body: g("t4_body", "Payout requests are reviewed and handled by a team, on a predictable schedule. You'll never chase a bot for your money.") },
];

const buildFaq = (g) => [
  { q: g("faq1_q", "Do I need to be technical?"), a: g("faq1_a", "No. If you can share a link, you're qualified. The product demos itself — your job is the introduction, ours is everything after the click.") },
  { q: g("faq2_q", "Does it cost anything to join?"), a: g("faq2_a", "Nothing. Registration is free, there's no minimum activity, and you'll never be asked to buy anything to stay a partner.") },
  { q: g("faq3_q", "When and how do I get paid?"), a: g("faq3_a", "Your earnings land in your wallet as your referrals pay. When you're ready, you request a payout from your panel and our team processes it on a regular schedule.") },
  { q: g("faq4_q", "I'm a creator, not a salesperson. Does this still work?"), a: g("faq4_a", "Especially then. You're not pitching — you're recommending something your audience actually thanks you for. One honest story about getting your inbox off your back does the selling.") },
];

export default function DemoLanding({ cms = null, brandOverride = null, testId = "demo-page", gOverride = null }) {
  const [brandFetched, setBrandFetched] = useState("Agentic Inbox");
  const brandName = brandOverride || brandFetched;
  const g = gOverride || ((k, fb) => (cms && cms[k]) || fb);
  const br = (s) => (s || "").split("{brand}").join(brandName);
  const nl = (s) => (s || "").split("\n").map((p, i, a) => i < a.length - 1 ? [p, <br key={i} />] : p);
  useEffect(() => {
    document.title = "Become a distributor — preview";
    window.scrollTo(0, 0);
    if (!brandOverride) distApi.get("/site/landing").then((r) => setBrandFetched(r.data?.brand?.app_name || "Agentic Inbox")).catch(() => {});
  }, []);

  return (
    <div className="demo-root min-h-screen" data-testid={testId}>
      {/* NAV */}
      <header className="demo-nav fixed top-0 inset-x-0 z-50 h-16">
        <div className="max-w-6xl mx-auto h-full px-6 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="demo-brand">
            <div className="w-7 h-7 rounded-lg bg-[#0071E3] flex items-center justify-center text-white"><Mail size={14} /></div>
            <span className="demo-heading font-semibold text-[15px] tracking-tight">{brandName}</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/distributors" data-testid="dl-nav-signin" className="text-[13px] text-[#1d1d1f]/70 hover:text-[#1d1d1f] transition-colors">{g("nav_signin", "Partner sign in")}</Link>
            <Link to="/distributors" data-testid="dl-nav-cta" className="demo-pill bg-[#0071E3] hover:bg-[#0077ED] text-white text-[13px] font-semibold px-4 py-2">{g("nav_cta", "Become a distributor")}</Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="pt-32 md:pt-40 pb-16 px-6">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.15fr_0.85fr] gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Label>{g("hero_eyebrow", "Partner Program")}</Label>
            <h1 data-testid="dl-hero-title" className="text-5xl md:text-7xl font-semibold tracking-tighter leading-[1.05] text-balance">
              {g("hero_title_l1", "One link.")}<br />{g("hero_title_l2", "Income on repeat.")}
            </h1>
            <p data-testid="dl-hero-sub" className="text-base md:text-lg text-[#1d1d1f]/60 mt-6 max-w-xl leading-relaxed">
              {br(g("hero_sub", "AI email is the hottest thing in software right now — and {brand} is the one people actually keep paying for. Seasoned reseller or rising creator: share your link, earn from day one."))}
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-9">
              <Link to="/distributors" data-testid="dl-hero-cta" className="demo-pill inline-flex items-center gap-2 bg-[#0071E3] hover:bg-[#0077ED] text-white text-[15px] font-semibold px-8 py-3.5">
                {g("hero_cta", "Get my link")} <ArrowRight size={15} />
              </Link>
              <a href="#how" data-testid="dl-hero-how" className="demo-pill inline-flex items-center gap-2 bg-white border border-black/10 text-[#1d1d1f] text-[15px] font-semibold px-8 py-3.5 hover:bg-black/[0.03]">
                {g("hero_how", "See how it works")}
              </a>
            </div>
          </motion.div>
          <LinkHeroMock g={g} />
        </div>
      </section>

      {/* MOMENTUM STRIP */}
      <section className="px-6 pb-20">
        <motion.div {...fadeUp} data-testid="dl-momentum" className="max-w-6xl mx-auto grid sm:grid-cols-3 gap-4">
          {[g("momentum_1", "Selling like hot cakes."), g("momentum_2", "People try it — and stay."), g("momentum_3", "You earn from day one.")].map((tItem, i) => (
            <div key={i} data-testid={`dl-momentum-${i + 1}`} className="demo-card px-6 py-5 flex items-center gap-3">
              <Sparkles size={16} className="text-[#0071E3] shrink-0" />
              <p className="text-[15px] font-medium tracking-tight">{tItem}</p>
            </div>
          ))}
        </motion.div>
      </section>

      {/* PERSONAS */}
      <section className="px-6 pb-24">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fadeUp}>
            <Label>{g("personas_label", "Two kinds of partners. One link.")}</Label>
            <h2 data-testid="dl-personas-title" className="text-4xl md:text-5xl font-medium tracking-tight leading-[1.12] text-balance mb-10">
              {g("personas_title", "Whichever one you are, you're early.")}
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-4">
            {buildPersonas(g).map(({ icon: Icon, testid, eyebrow, title, points }, i) => (
              <motion.div key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 * i }}
                data-testid={testid} className="demo-card p-8 md:p-10">
                <div className="w-11 h-11 rounded-2xl bg-[#0071E3]/10 text-[#0071E3] flex items-center justify-center mb-5"><Icon size={19} /></div>
                <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#86868B] mb-3">{eyebrow}</p>
                <h3 className="demo-heading text-2xl font-medium tracking-tight leading-snug mb-5">{title}</h3>
                <ul className="space-y-3">
                  {points.map((p, k) => (
                    <li key={k} className="flex gap-2.5 text-[14px] text-[#1d1d1f]/65 leading-relaxed">
                      <Check size={15} className="text-[#0071E3] mt-0.5 shrink-0" /> {p}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="px-6 pb-24">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fadeUp}>
            <Label>{g("how_label", "How it works")}</Label>
            <h2 className="text-4xl md:text-5xl font-medium tracking-tight leading-[1.12] mb-10">{g("how_title", "Three steps. No catch.")}</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-4">
            {buildSteps(g).map(({ n, title, body }, i) => (
              <motion.div key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 * i }}
                data-testid={`dl-step-${i + 1}`} className="demo-card p-8 relative overflow-hidden">
                <span className="demo-heading absolute -top-3 right-4 text-[88px] font-semibold leading-none text-[#0071E3]/[0.07] select-none">{n}</span>
                <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#0071E3] mb-3">{g("step_word", "Step")} {n}</p>
                <h3 className="demo-heading text-xl font-medium tracking-tight mb-2.5">{title}</h3>
                <p className="text-sm text-[#1d1d1f]/60 leading-relaxed">{body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GROWTH NARRATIVE (dark) — kept */}
      <section className="px-6 pb-24">
        <motion.div {...fadeUp} data-testid="dl-growth" className="max-w-6xl mx-auto demo-card demo-dark p-10 md:p-14 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <Label light><span className="inline-flex items-center gap-2"><TrendingUp size={13} /> {g("growth_label", "The compounding part")}</span></Label>
            <h2 className="demo-heading text-3xl md:text-4xl font-medium tracking-tight leading-tight text-white">{g("growth_title", "Your earnings compound.")}</h2>
            <p className="text-[15px] text-white/60 mt-4 leading-relaxed">
              {g("growth_p1", "This isn't a one-off bounty. Every referral who stays subscribed keeps paying — and keeps paying you. Refer one business this month and you've planted something. Refer one every month and watch the lines stack.")}
            </p>
            <p className="text-[15px] text-white/60 mt-3 leading-relaxed">
              {g("growth_p2", "The work you do today is still paying you next quarter. That's the difference between a gig and a portfolio.")}
            </p>
          </div>
          <GrowthMock />
        </motion.div>
      </section>

      {/* WHAT YOU'LL BE SHARING — visual */}
      <section className="px-6 pb-24">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeUp}>
            <Label>{g("share_label", "What you'll be sharing")}</Label>
            <h2 data-testid="dl-why-title" className="text-4xl md:text-5xl font-medium tracking-tight leading-[1.12] text-balance">
              {br(g("share_title", "Why {brand} sells itself."))}
            </h2>
            <p className="text-[15px] text-[#1d1d1f]/60 mt-5 max-w-lg leading-relaxed">
              {g("share_p1", "An AI agent that answers email from WhatsApp, briefs its owner every morning and writes in their exact tone. Your prospect doesn't watch a demo — they text their own inbox and feel it.")}
            </p>
            <p className="demo-heading text-xl font-medium tracking-tight mt-6 text-[#1d1d1f]">
              {nl(g("share_quote", "You're not selling software.\nYou're giving people their evenings back."))}
            </p>
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.12 }}><ChatMock /></motion.div>
        </div>
      </section>

      {/* TRUST — kept */}
      <section className="px-6 pb-24">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fadeUp}>
            <Label>{g("trust_label", "Built on trust")}</Label>
            <h2 data-testid="dl-trust-title" className="text-4xl md:text-5xl font-medium tracking-tight leading-[1.12] mb-10 text-balance">
              {g("trust_title", "You'll never wonder where you stand.")}
            </h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-4">
            {buildTrust(g).map(({ icon: Icon, title, body }, i) => (
              <motion.div key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.06 * i }}
                data-testid={`dl-trust-${i + 1}`} className="demo-card p-7 flex gap-4">
                <div className="w-10 h-10 rounded-2xl bg-[#0071E3]/10 text-[#0071E3] flex items-center justify-center shrink-0"><Icon size={17} /></div>
                <div>
                  <h3 className="demo-heading text-[16px] font-medium tracking-tight mb-1.5">{title}</h3>
                  <p className="text-sm text-[#1d1d1f]/60 leading-relaxed">{body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 pb-24">
        <div className="max-w-3xl mx-auto">
          <motion.div {...fadeUp}>
            <Label>{g("faq_label", "Questions, answered")}</Label>
            <h2 className="text-4xl md:text-5xl font-medium tracking-tight leading-[1.12] mb-8">{g("faq_title", "Before you ask.")}</h2>
          </motion.div>
          <div className="space-y-3">
            {buildFaq(g).map(({ q, a }, i) => (
              <motion.details key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.05 * i }}
                data-testid={`dl-faq-${i + 1}`} className="demo-card px-6 py-4 cursor-pointer group">
                <summary className="demo-heading text-[15px] font-medium tracking-tight list-none flex items-center justify-between">
                  {q}<span className="text-[#86868B] group-open:rotate-45 transition-transform text-lg leading-none">+</span>
                </summary>
                <p className="text-sm text-[#1d1d1f]/60 leading-relaxed mt-3">{a}</p>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      {/* DARK CTA */}
      <section className="px-6 pb-10">
        <motion.div {...fadeUp} data-testid="dl-cta" className="max-w-6xl mx-auto demo-card demo-dark p-12 md:p-16 text-center">
          <h2 className="demo-heading text-3xl md:text-5xl font-medium tracking-tight text-white leading-tight text-balance">
            {nl(g("cta_title", "The product is ready.\nThe market is hungry. Are you?"))}
          </h2>
          <p className="text-[15px] text-white/55 mt-4 max-w-xl mx-auto">{g("cta_sub", "Your link takes 60 seconds. The first share takes one message.")}</p>
          <Link to="/distributors" data-testid="dl-cta-btn" className="demo-pill inline-flex items-center gap-2 bg-white text-[#1d1d1f] text-[15px] font-semibold px-8 py-3.5 mt-8">
            {g("cta_btn", "Get my link")} <ArrowRight size={15} />
          </Link>
        </motion.div>
      </section>

      {/* FOOTER */}
      <footer data-testid="dl-footer" className="px-6 pb-10">
        <div className="max-w-6xl mx-auto pt-8 border-t border-black/5 flex flex-col sm:flex-row items-center justify-between gap-3 text-[12px] text-[#86868B]">
          <p>© 2026 {brandName}. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <Link to="/distributors/terms" className="hover:text-[#1d1d1f] transition-colors">{g("footer_terms", "Distributor Terms")}</Link>
            <Link to="/distributors/privacy" className="hover:text-[#1d1d1f] transition-colors">{g("footer_privacy", "Distributor Privacy")}</Link>
            <Link to="/" className="hover:text-[#1d1d1f] transition-colors">{br(g("footer_back", "Back to {brand}"))}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
